import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ButtonComponent from '../../components/ButtonComponent';
import InputField from '../../components/form/InputField';
import useFormInput from '../../custom-hooks/useFormInput';
import AuthLayout from '../../layouts/AuthLayout';
import { LuMail, LuLock } from 'react-icons/lu';
import { FacebookIcon, GoogleIcon } from '../../components/Icons';
import { SITELINK } from '../../app-routes/Links';
import { z } from 'zod';
import auth from '../../api-services/auth/authentication.services';
import { encryptDataWithIV, getUser, storeToken, storeUser } from '../../app-utils';
import { FEED_MSSG, RES_MSSG } from '../../api-services/ConstMssg';
import { GOOGLE_URL } from '../../api-services/process';
import {
  MembersLoginDefault,
  MembersLoginSchema,
  MembersLoginType,
  MembersLoginResponse,
} from '../../app-schemas/authentication.schema';
import { useTheme } from '../../context/theme-context';

// main login component
const UserLogin = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [buttonStyle, setButtonStyle] = useState<string>('fixed');

  // custom hook to get && validate users input
  const [formData, handleChange, errors, setValidationErrors] = useFormInput(
    MembersLoginDefault,
    MembersLoginSchema,
  );

  const inputFocus = () => { setButtonStyle('relative'); };
  const inputBlur = () => { setButtonStyle('fixed'); };

  // Check if user has been fully onboarded
  useEffect(() => {
    const token = localStorage.getItem('aa_public') || '';
    if (!!token) {
      const user = getUser() || null;
      if (user) {
        if (user.is_onboarded) navigate(SITELINK.HOME);
        else navigate(SITELINK.LOCATION_SETUP);
      }
    }
  }, [navigate]);

  // login with email and password
  const handleEmailLogin = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    const { success, data, error } = MembersLoginSchema.safeParse(formData);

    if (success) {
      await EmailLogin(data);
    } else {
      handleValidationErrors(error);
    }
  };

  // email login business logic
  const EmailLogin = async (data: MembersLoginType) => {
    setLoading(true);

    try {
      const res = await auth.EmailLogin(data);

      if (res?.data?.message === RES_MSSG.SUCCESS) {
        handleLoginSuccess(res.data);
      } else {
        handleLoginError(res?.response?.data?.message);
      }
    } catch (error) {
      setErrorMsg('An error occured!');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginError = (message: string) => {
    switch (message) {
      case RES_MSSG.EMAIL_NOT_VERIFIED:
        setErrorMsg(FEED_MSSG.EMAIL_NOT_VERIFIED);
        const plainText = JSON.stringify(formData);
        const encryptedText = encryptDataWithIV(plainText);
        localStorage.setItem('forVerify', encryptedText);
        navigate(SITELINK.AUTH_2FA);
        break;

      case RES_MSSG.INCORRECT_CREDENTIALS:
        setErrorMsg(FEED_MSSG.INCORRECT_CREDENTIALS);
        break;

      default:
        setErrorMsg(message);
        break;
    }
  };

  const handleLoginSuccess = (res: MembersLoginResponse) => {
    const responseData = res;
    const userInfo = responseData.user;

    storeToken(responseData.data);
    storeUser(userInfo);

    if (userInfo.is_onboarded) {
      const loginRedirectedFromUrl = localStorage.getItem('loginRedirectedFromUrl');
      localStorage.removeItem('loginRedirectedFromUrl');
      navigate(loginRedirectedFromUrl ?? SITELINK.HOME);
    } else {
      navigate(SITELINK.LOCATION_SETUP);
    }
  };

  const handleFacebookLogin = () => {
    // 1198011421448129
    // window.location.href =
    // "https://www.facebook.com/v20.0/dialog/oauth/?client_id=1198011421448129&redirect_uri=" +
    // fbDecodedRedirectURI +
    // "&state={st%3Dstate123abc%2Cds%3D123456789}";
  };

  const handleValidationErrors = (error: z.ZodError<MembersLoginType>) => {
    const newErrors: Partial<Record<keyof MembersLoginType, string>> = {};
    error.errors.forEach((err) => {
      newErrors[err.path[0] as keyof MembersLoginType] = err.message;
    });

    setValidationErrors(newErrors);
  };

  const handleGoogleLogin = () => {
    window.location.href = GOOGLE_URL;
  };

  return (
    <section className={`mt-3 min-h-screen ${isDarkMode ? 'bg-neutral-800' : ''}`}>
      <AuthLayout title="Catapot - Discover cultures and communities">
        {/* welcome flag */}
        <div className="flex flex-col items-start mt-2">
          <h1 className="text-[32px] font-[400] text-black dark:text-white madimi-one flex gap-1 items-center">
            <span>Welcome!</span>

            <svg
              width="30"
              height="30"
              viewBox="0 0 30 30"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21.8673 14.0625C21.9142 14.1328 22.0314 14.0859 22.0314 14.0156C22.172 12.75 22.547 9.25781 25.0314 8.50781C25.8283 8.27343 26.6251 8.83593 26.7189 9.5625C26.883 10.7344 25.547 13.7578 25.6642 16.5234C25.6876 16.9687 26.4376 21.6797 24.4455 24.4687C22.4533 27.2578 17.7189 30.0937 13.008 25.7812C10.5705 23.5547 10.5705 22.6641 7.54702 19.5469C6.93765 18.9375 4.50015 16.2656 3.8439 15.4453C2.97671 14.3437 4.35952 12.8906 5.41421 13.6406C5.9064 13.9922 10.2658 17.6484 10.4298 17.8125C10.758 18.0937 11.1564 17.6953 10.922 17.4141C8.25015 13.8984 5.67202 10.7344 4.9689 9.51562C4.26577 8.29687 5.95327 6.98437 6.93765 8.03906C7.61734 8.74218 12.6564 14.625 12.8908 14.8828C13.1251 15.1406 13.5236 14.8125 13.383 14.4844C13.2189 14.1797 9.14077 7.07812 8.41421 5.46093C7.7814 4.03125 9.89077 2.69531 10.8751 4.17187C11.672 5.34375 16.1251 12.75 16.2892 13.0078C16.5001 13.3828 16.9689 13.1484 16.7814 12.7266C16.6408 12.4219 13.8986 4.92187 13.6876 4.14843C13.3126 2.78906 15.258 1.66406 16.172 3.21093C17.4142 5.34375 19.8751 11.8594 21.8673 14.0625Z"
                fill="#FFCA28"
              />
              <mask id="mask0_37_778" maskUnits="userSpaceOnUse" x="3" y="2" width="24" height="26">
                <path
                  d="M21.8673 14.0625C21.9142 14.1328 22.0314 14.0859 22.0314 14.0156C22.172 12.75 22.547 9.25781 25.0314 8.50781C25.8283 8.27343 26.6251 8.83593 26.7189 9.5625C26.883 10.7344 25.547 13.7578 25.6642 16.5234C25.6876 16.9687 26.4376 21.6797 24.4455 24.4687C22.4533 27.2578 17.7189 30.0937 13.008 25.7812C10.5705 23.5547 10.5705 22.6641 7.54702 19.5469C6.93765 18.9375 4.50015 16.2656 3.8439 15.4453C2.97671 14.3437 4.35952 12.8906 5.41421 13.6406C5.9064 13.9922 10.2658 17.6484 10.4298 17.8125C10.758 18.0937 11.1564 17.6953 10.922 17.4141C8.25015 13.8984 5.67202 10.7344 4.9689 9.51562C4.26577 8.29687 5.95327 6.98437 6.93765 8.03906C7.61734 8.74218 12.6564 14.625 12.8908 14.8828C13.1251 15.1406 13.5236 14.8125 13.383 14.4844C13.2189 14.1797 9.14077 7.07812 8.41421 5.46093C7.7814 4.03125 9.89077 2.69531 10.8751 4.17187C11.672 5.34375 16.1251 12.75 16.2892 13.0078C16.5001 13.3828 16.9689 13.1484 16.7814 12.7266C16.6408 12.4219 13.8986 4.92187 13.6876 4.14843C13.3126 2.78906 15.258 1.66406 16.172 3.21093C17.4142 5.34375 19.8751 11.8594 21.8673 14.0625Z"
                  fill="white"
                />
              </mask>
              <g mask="url(#mask0_37_778)">
                <path
                  d="M21.3753 14.1094C18.469 16.4766 17.18 19.8516 19.5706 23.3672C19.7815 23.6719 20.2268 23.3906 20.0393 23.0391C19.055 21.2812 18.3284 18.1172 21.7268 14.8125C22.1253 15 22.594 14.6016 22.6175 14.25C22.8753 12.0469 23.1331 10.2187 24.7737 9.18749C25.4065 8.78905 26.2268 9.0703 26.2737 10.0312C26.2737 10.0312 26.7893 10.0781 27.0003 10.1016C27.1175 9.60937 27.3518 8.71874 27.3518 8.6953C27.3518 8.67187 24.5862 7.64062 24.5862 7.64062L22.1721 10.0312L21.3753 14.1094ZM16.6878 13.1484C17.0159 12.9844 17.2971 12.7266 17.1565 12.375C17.0159 12 14.4143 4.92187 14.2503 4.3828C13.9221 3.39843 14.7425 2.62499 15.469 2.95312C15.8675 3.14062 16.055 2.5078 15.844 2.41405C15.6331 2.3203 15.1409 1.87499 14.1565 2.36718C12.3987 3.2578 13.43 5.22655 13.8987 6.37499C14.3675 7.52343 16.6878 13.1484 16.6878 13.1484ZM13.2893 14.9062C13.6878 14.625 13.9925 14.3906 13.6409 13.8281C13.4065 13.4531 9.44558 6.2578 9.04714 5.55468C8.4612 4.54687 9.67995 3.65624 10.3596 4.28905C10.6643 4.5703 11.1331 3.96093 10.8987 3.72655C10.6643 3.49218 9.84402 2.81249 8.81277 3.49218C7.78152 4.17187 7.75808 4.99218 7.8987 5.34374C8.01589 5.6953 13.2893 14.9062 13.2893 14.9062ZM10.9456 17.7891C11.2268 17.3672 11.4143 17.1562 11.0159 16.6172C10.6175 16.0781 5.88308 9.89062 5.62527 9.53905C4.96902 8.67187 5.92995 7.92187 6.3987 8.17968C6.84402 8.43749 7.10183 7.71093 6.72683 7.52343C6.35183 7.33593 5.74245 7.12499 5.17995 7.52343C4.61745 7.92187 4.28933 8.74218 4.38308 9.21093C4.45339 9.67968 10.9456 17.7891 10.9456 17.7891Z"
                  fill="#FAA700"
                />
                <path
                  d="M21.7504 26.625C17.9535 28.4297 15.1176 27.0234 13.2894 25.2422C11.2035 23.2031 10.2191 21.3281 8.83632 20.0625C8.36757 19.6172 4.66444 15.6797 4.21913 15C3.86757 14.4375 4.57069 13.4766 5.34413 14.0156C5.83632 14.3437 6.07069 13.6406 5.74257 13.4531C5.41444 13.2656 4.68788 12.8672 3.91444 13.4531C3.14101 14.0156 3.11757 15.0234 3.30507 15.3516C3.49257 15.6797 14.8832 28.7578 14.8832 28.7578L19.5238 28.6406L22.0082 27.1172L21.7504 26.625Z"
                  fill="#FAA700"
                />
              </g>
              <mask id="mask1_37_778" maskUnits="userSpaceOnUse" x="3" y="2" width="24" height="26">
                <path
                  d="M21.8673 14.0625C21.9142 14.1328 22.0314 14.0859 22.0314 14.0156C22.172 12.75 22.547 9.25781 25.0314 8.50781C25.8283 8.27343 26.6251 8.83593 26.7189 9.5625C26.883 10.7344 25.547 13.7578 25.6642 16.5234C25.6876 16.9687 26.4376 21.6797 24.4455 24.4687C22.4533 27.2578 17.7189 30.0937 13.008 25.7812C10.5705 23.5547 10.5705 22.6641 7.54702 19.5469C6.93765 18.9375 4.50015 16.2656 3.8439 15.4453C2.97671 14.3437 4.35952 12.8906 5.41421 13.6406C5.9064 13.9922 10.2658 17.6484 10.4298 17.8125C10.758 18.0937 11.1564 17.6953 10.922 17.4141C8.25015 13.8984 5.67202 10.7344 4.9689 9.51562C4.26577 8.29687 5.95327 6.98437 6.93765 8.03906C7.61734 8.74218 12.6564 14.625 12.8908 14.8828C13.1251 15.1406 13.5236 14.8125 13.383 14.4844C13.2189 14.1797 9.14077 7.07812 8.41421 5.46093C7.7814 4.03125 9.89077 2.69531 10.8751 4.17187C11.672 5.34375 16.1251 12.75 16.2892 13.0078C16.5001 13.3828 16.9689 13.1484 16.7814 12.7266C16.6408 12.4219 13.8986 4.92187 13.6876 4.14843C13.3126 2.78906 15.258 1.66406 16.172 3.21093C17.4142 5.34375 19.8751 11.8594 21.8673 14.0625Z"
                  fill="white"
                />
              </mask>
              <g mask="url(#mask1_37_778)">
                <path
                  d="M26.1331 8.4375C26.2503 8.74219 26.3909 9.28125 26.2503 10.0078C25.7113 12.7266 24.8206 14.3203 25.1019 17.25C25.6175 22.875 23.6488 27.1406 16.8753 27.9844C16.1956 28.0781 16.3831 28.5938 16.7347 28.5469C16.8988 28.5234 21.7269 27.6562 21.7269 27.6562C21.7269 27.6562 26.2034 23.8359 26.2034 23.7891C26.2034 23.7422 27.3284 9.16406 27.3284 9.16406L26.1331 8.4375ZM14.4144 2.46094C14.7191 2.50781 15.3753 2.76562 15.8441 3.67969C16.3128 4.59375 18.6097 9.86719 18.9144 10.4297C19.2191 10.9922 20.4613 13.7812 21.4691 14.625C21.7972 14.8828 21.9613 14.4375 21.9613 14.4375L21.9847 13.7109L16.5472 1.33594L14.7425 1.40625L14.4144 2.46094ZM16.6878 13.1484C16.2425 13.3594 15.9144 13.5469 15.5862 13.0312C15.3753 12.6562 13.1019 8.76562 10.2425 4.21875C10.0081 3.86719 9.46906 3.5625 9.04719 3.72656C8.62531 3.91406 8.53156 3.375 8.7425 3.25781C8.95344 3.14062 10.2659 2.41406 11.1566 3.91406C12.0472 5.41406 16.6878 13.1484 16.6878 13.1484ZM13.2894 14.9062C12.9378 15.1641 12.5862 15.4922 12.1409 14.9531C11.7191 14.4375 6.91438 8.71875 6.63313 8.36719C6.35188 8.01562 5.76594 7.71094 5.32063 7.99219C5.01594 8.17969 4.73469 7.61719 4.92219 7.47656C5.10969 7.33594 6.16438 6.39844 6.8675 7.19531C7.57062 7.99219 13.2894 14.9062 13.2894 14.9062ZM10.9456 17.7891C10.6409 18.1172 10.3128 18.3047 9.8675 17.9766C9.42219 17.6484 5.43781 14.2969 5.18 14.0625C4.92219 13.8281 4.17219 13.4766 3.60969 14.0391C3.39875 14.25 3.02375 13.8984 3.23469 13.6875C3.44563 13.4531 4.59406 12.2812 5.50813 13.0547C6.42219 13.8281 10.9456 17.7891 10.9456 17.7891Z"
                  fill="#B55E19"
                />
              </g>
              <path
                d="M2.48457 19.0547C3.21114 21.2109 4.3127 22.5469 6.11739 23.7656C6.44551 24 6.25801 24.4687 5.88301 24.3047C4.19551 23.625 2.32051 22.125 1.8752 19.1484C1.80489 18.75 2.36739 18.6797 2.48457 19.0547ZM3.89082 17.7656C4.61739 19.9219 5.71895 21.2578 7.52364 22.4766C7.85176 22.7109 7.66426 23.1797 7.28926 23.0156C5.60176 22.3359 3.72676 20.8359 3.28145 17.8594C3.23457 17.4609 3.77364 17.3906 3.89082 17.7656ZM21.9143 8.10937C21.3518 5.90624 20.3205 4.5 18.6096 3.1875C18.2814 2.95312 18.5158 2.48437 18.8908 2.64843C20.5314 3.42187 22.3127 5.0625 22.5471 8.0625C22.5705 8.46093 22.008 8.50781 21.9143 8.10937ZM23.4377 6.98437C22.8752 4.78124 21.8439 3.375 20.133 2.0625C19.8049 1.82812 20.0393 1.35937 20.4143 1.52343C22.0549 2.29687 23.8361 3.9375 24.0705 6.9375C24.0939 7.3125 23.5549 7.35937 23.4377 6.98437Z"
                fill="#B0BEC5"
              />
            </svg>
          </h1>

          <p className="text-[12px] font-[700] text-gray-600 dark:text-white/80">
            Join communities and discover cultures
          </p>
        </div>

        {/* OR divider */}
        <div className="mt-5 text-center font-semibold text-xs text-black dark:text-white">
          <p className={`inline px-3 relative top-[9px] text-[12px] font-[700] ${isDarkMode ? 'bg-neutral-800 text-white' : 'bg-white text-black'}`}>
            Continue with
          </p>
          <p className={`border-b ${isDarkMode ? 'border-neutral-600' : 'border-[#ccc]'}`}></p>
        </div>

        {/* social-media login */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={handleGoogleLogin}
            className={`w-full text-[12px] flex gap-2 items-center rounded-md border border-1 px-4 py-2 hover:shadow-xl font-[700] transition-colors ${
              isDarkMode 
                ? 'bg-neutral-800 border-neutral-600 text-white hover:bg-neutral-700' 
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <GoogleIcon />
            <span>Google</span>
          </button>

          <button
            className={`w-full text-[12px] flex gap-2 items-center rounded-md border border-1 px-4 py-2 hover:shadow-xl font-[700] transition-colors ${
              isDarkMode 
                ? 'bg-neutral-800 border-neutral-600 text-white hover:bg-neutral-700' 
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => handleFacebookLogin()}
          >
            <FacebookIcon />
            <span>Facebook</span>
          </button>
        </div>

        {/* OR divider */}
        <div className="mt-5 text-center font-semibold text-xs text-black dark:text-white">
          <p className={`inline px-3 relative top-[9px] text-[12px] font-[700] ${isDarkMode ? 'bg-neutral-800 text-white' : 'bg-white text-black'}`}>
            Or
          </p>
          <p className={`border-b ${isDarkMode ? 'border-neutral-600' : 'border-[#ccc]'}`}></p>
        </div>

        {/* email login form area */}
        <form onSubmit={handleEmailLogin} className="grid grid-cols-2 gap-3 mt-6 relative">
          <div className="text-red-500 dark:text-red-400 text-[10px] col-span-full text-center">{errorMsg}</div>

          <div className="col-span-full">
            <InputField
              className="col-span-full text-[12px] rounded-md font-medium dark:bg-neutral-800 dark:text-white/80 dark:border-neutral-600"
              type="email"
              id="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              onFocus={inputFocus}
              onBlur={inputBlur}
              icon={LuMail}
              error={errors?.email}
              darkMode={isDarkMode}
            />
          </div>

          <div className="col-span-full">
            <InputField
              className="col-span-full text-[12px] rounded-md font-medium dark:bg-neutral-800 dark:text-white/80 dark:border-neutral-600"
              type="password"
              id="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              onFocus={inputFocus}
              onBlur={inputBlur}
              icon={LuLock}
              error={errors?.password}
              darkMode={isDarkMode}
            />

            <div className="text-end mt-2">
              <Link
                className="text-[12px] font-[400] madimi-one text-blue-600 dark:text-blue-400 hover:underline"
                to={SITELINK.REQUEST_RESET}
              >
                Forgot password?
              </Link>
            </div>
          </div>

          <div className={`${buttonStyle} right-0 left-0 bottom-0 w-full col-span-full ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
            <div className={`${buttonStyle === 'fixed' && 'px-6'} w-full`}>
              {/* action btn */}
              <ButtonComponent
                type="submit"
                className="mt-4 text-[14px] font-[700] py-3 rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors"
                disabled={loading}
                loading={loading}
              >
                Continue
              </ButtonComponent>

              {/* S4 aggrement area with sign-in link */}
              <div className="col-span-full">
                <p className="text-[12px] text-center text-black dark:text-white/80 mb-4 mt-4 font-medium">
                  By clicking "Continue", you agree to our
                  <br />
                  <Link to="/privacy-policy" className="underline text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link to="/terms-of-services" className="underline text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                    Terms of Service
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </form>
      </AuthLayout>
    </section>
  );
};

export default UserLogin;